/**
 * Modal do sino de Eventos & Alarmes — header /cliente.
 *
 * Mostra a lista de dispositivos com alarmes ATIVOS (não reconhecidos),
 * agrupados por dispositivo em collapsibles (default fechado). Cada
 * dispositivo tem um botão "Resetar Alarmes" alinhado à direita do título
 * que reconhece todos os alarmes daquele dispositivo na Cloud e enfileira
 * o comando no BSS-Edge.
 */
import { useEffect, useState } from "react";
import { X, ChevronDown, ChevronRight, AlertTriangle, RotateCcw, Check, Loader2 } from "lucide-react";
import clienteApi from "@/lib/clienteApi";

export default function EventosAlarmesBellModal({ open, onClose, onReset }) {
  const [loading, setLoading] = useState(false);
  const [devices, setDevices] = useState([]);
  const [openIds, setOpenIds] = useState({});         // {dispositivo_id: true}
  const [eventsByDevice, setEventsByDevice] = useState({});
  const [resetingId, setResetingId] = useState(null);
  const [ackingId, setAckingId] = useState(null);

  const loadBadge = async () => {
    setLoading(true);
    try {
      const r = await clienteApi.get("/cliente/eventoalarmes/badge");
      setDevices(r.data.devices || []);
    } catch (e) {
      console.error("badge:", e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (open) loadBadge(); }, [open]);

  const toggle = async (devId) => {
    const isOpen = !!openIds[devId];
    setOpenIds((s) => ({ ...s, [devId]: !isOpen }));
    if (!isOpen && !eventsByDevice[devId]) {
      try {
        const r = await clienteApi.get(`/cliente/eventoalarmes/badge/devices/${devId}`);
        setEventsByDevice((s) => ({ ...s, [devId]: r.data.items || [] }));
      } catch (e) {
        console.error("device events:", e);
      }
    }
  };

  const handleReset = async (devId, devDesc) => {
    if (!window.confirm(`Resetar todos os alarmes de "${devDesc}"?\n\n• Marca alarmes como "Reconhecidos" na nuvem.\n• Envia comando para o BSS-Edge cancelar alarmes no equipamento físico.\n• Eventos de porta NÃO são afetados.`)) return;
    setResetingId(devId);
    try {
      const r = await clienteApi.post(`/cliente/eventoalarmes/reset/${devId}`);
      await loadBadge();
      setEventsByDevice((s) => ({ ...s, [devId]: undefined }));
      setOpenIds((s) => ({ ...s, [devId]: false }));
      if (onReset) onReset(r.data);
    } catch (e) {
      alert("Falha ao resetar: " + (e.response?.data?.detail || e.message));
    } finally {
      setResetingId(null);
    }
  };

  const handleAcknowledge = async (devId, devDesc) => {
    if (!window.confirm(`Reconhecer os alarmes de "${devDesc}"?\n\n• Marca alarmes como "Reconhecidos" APENAS na nuvem.\n• NÃO envia comando ao BSS-Edge nem ao equipamento físico.\n• Use quando o alarme já foi normalizado fisicamente.`)) return;
    setAckingId(devId);
    try {
      const r = await clienteApi.post(`/cliente/eventoalarmes/acknowledge/${devId}`);
      await loadBadge();
      setEventsByDevice((s) => ({ ...s, [devId]: undefined }));
      setOpenIds((s) => ({ ...s, [devId]: false }));
      if (onReset) onReset(r.data);
    } catch (e) {
      alert("Falha ao reconhecer: " + (e.response?.data?.detail || e.message));
    } finally {
      setAckingId(null);
    }
  };

  if (!open) return null;

  return (
    <div
      data-testid="eventoalarme-bell-modal"
      className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[60] flex items-start justify-center p-4 pt-20"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="bg-brand-dark/95 border border-red-400/40 rounded-xl w-full max-w-2xl max-h-[80vh] overflow-hidden flex flex-col shadow-2xl">
        <div className="flex items-center justify-between px-5 py-4 border-b border-red-400/20">
          <h3 className="flex items-center gap-2 text-base font-bold text-white">
            <AlertTriangle className="w-5 h-5 text-red-400" />
            Alarmes Ativos
            <span className="text-xs font-normal text-red-200/60 ml-1">
              ({devices.reduce((s, d) => s + d.count, 0)} alarme(s) em {devices.length} dispositivo(s))
            </span>
          </h3>
          <button
            onClick={onClose}
            data-testid="eventoalarme-bell-modal-close"
            className="text-white/60 hover:text-white text-xl leading-none"
          >×</button>
        </div>

        <div className="overflow-y-auto flex-1 p-4 space-y-2">
          {loading && (
            <div className="flex items-center justify-center py-10 text-white/50 text-sm gap-2">
              <Loader2 className="w-4 h-4 animate-spin" /> Carregando...
            </div>
          )}
          {!loading && devices.length === 0 && (
            <div className="text-center py-10 text-white/50 text-sm italic">
              Nenhum alarme ativo no momento.
            </div>
          )}
          {!loading && devices.map((d) => {
            const isOpen = !!openIds[d.dispositivo_id];
            const events = eventsByDevice[d.dispositivo_id] || [];
            return (
              <div
                key={d.dispositivo_id || d.dispositivo_sn}
                data-testid={`eventoalarme-device-${d.dispositivo_id}`}
                className="bg-black/30 border border-red-400/20 rounded-lg overflow-hidden"
              >
                <div className="flex items-center gap-2 px-3 py-2.5">
                  <button
                    onClick={() => toggle(d.dispositivo_id)}
                    className="flex items-center gap-2 flex-1 min-w-0 text-left text-white hover:text-red-200 transition-colors"
                    data-testid={`eventoalarme-device-toggle-${d.dispositivo_id}`}
                  >
                    {isOpen
                      ? <ChevronDown className="w-4 h-4 shrink-0" />
                      : <ChevronRight className="w-4 h-4 shrink-0" />}
                    <span className="font-mono text-xs text-red-200/70">
                      {String(d.n_coletor ?? 0).padStart(3, "0")}
                    </span>
                    <span className="text-sm font-medium truncate">{d.dispositivo_desc || d.dispositivo_sn}</span>
                    <span className="ml-1 inline-flex items-center justify-center min-w-[24px] h-5 px-1.5 text-[11px] font-bold text-white bg-red-500 rounded-full">
                      {d.count}
                    </span>
                  </button>
                  <button
                    onClick={() => handleAcknowledge(d.dispositivo_id, d.dispositivo_desc || d.dispositivo_sn)}
                    disabled={ackingId === d.dispositivo_id || resetingId === d.dispositivo_id}
                    className="shrink-0 flex items-center gap-1.5 bg-emerald-500/20 hover:bg-emerald-500/30 border border-emerald-400/40 text-emerald-100 font-bold px-2.5 py-1 rounded text-[11px] uppercase tracking-wider disabled:opacity-40"
                    data-testid={`eventoalarme-device-ack-${d.dispositivo_id}`}
                    title="Reconhecer Alarmes — marca como reconhecido APENAS na nuvem (sem enviar comando ao equipamento)"
                  >
                    {ackingId === d.dispositivo_id
                      ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      : <Check className="w-3.5 h-3.5" />}
                    Reconhecer
                  </button>
                  <button
                    onClick={() => handleReset(d.dispositivo_id, d.dispositivo_desc || d.dispositivo_sn)}
                    disabled={resetingId === d.dispositivo_id || ackingId === d.dispositivo_id}
                    className="shrink-0 flex items-center gap-1.5 bg-amber-500/20 hover:bg-amber-500/30 border border-amber-400/40 text-amber-100 font-bold px-2.5 py-1 rounded text-[11px] uppercase tracking-wider disabled:opacity-40"
                    data-testid={`eventoalarme-device-reset-${d.dispositivo_id}`}
                    title="Resetar Alarmes — reconhece na nuvem E envia comando ao equipamento físico"
                  >
                    {resetingId === d.dispositivo_id
                      ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      : <RotateCcw className="w-3.5 h-3.5" />}
                    Resetar Alarmes
                  </button>
                </div>
                {isOpen && (
                  <div className="border-t border-red-400/15 bg-black/20 px-3 py-2 text-xs">
                    {!events.length && (
                      <p className="text-white/40 italic py-1">Sem detalhes — atualizando…</p>
                    )}
                    {events.map((ev) => (
                      <div key={ev.id} className="flex items-center justify-between gap-3 py-1 border-b border-white/5 last:border-0">
                        <div className="flex-1 min-w-0">
                          <p className="text-red-100 font-medium truncate">{ev.descricao}</p>
                          <p className="text-white/40 text-[10px] font-mono">
                            {ev.data_evento} · porta {ev.door} · {ev.tipo_alarme}
                          </p>
                        </div>
                        <span className="text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded border bg-red-500/30 border-red-400/60 text-red-100">
                          {ev.status}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <div className="px-4 py-2 border-t border-white/10 text-[10px] text-white/40 flex items-center justify-between">
          <span>Reset reconhece alarmes na Cloud + envia comando ao BSS-Edge local (latência ~30 s).</span>
          <button
            onClick={loadBadge}
            className="text-sky-300 hover:text-sky-200 underline"
            data-testid="eventoalarme-bell-modal-refresh"
          >Atualizar</button>
        </div>
      </div>
    </div>
  );
}
